import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useSupportFAQs } from '@/hooks/use-support-faqs';
import { Skeleton } from '@/components/ui/skeleton';
import DOMPurify from 'dompurify';

interface FAQListProps {
  compact?: boolean;
}

export function FAQList({ compact = false }: FAQListProps) {
  const { data: faqs, isLoading } = useSupportFAQs();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (!faqs || faqs.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Nenhum FAQ disponível.</p>
      </div>
    );
  }

  const displayFaqs = compact ? faqs.slice(0, 5) : faqs;

  return (
    <div className={compact ? '' : 'space-y-6'}>
      {!compact && (
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Perguntas Frequentes</h2>
          <p className="text-muted-foreground">
            Encontre respostas para as dúvidas mais comuns
          </p>
        </div>
      )}

      <Accordion type="single" collapsible className="w-full">
        {displayFaqs.map((faq, index) => (
          <AccordionItem key={faq.id} value={`item-${index}`}>
            <AccordionTrigger className="text-left">
              <div className="space-y-1">
                <div className="font-medium">{faq.question}</div>
                {faq.support_categories && (
                  <div className="text-xs text-muted-foreground">
                    {faq.support_categories.name}
                  </div>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="prose prose-sm max-w-none">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(faq.answer, {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
                      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
                    }) 
                  }} 
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {compact && faqs.length > 5 && (
        <div className="text-center pt-4">
          <p className="text-sm text-muted-foreground">
            E mais {faqs.length - 5} FAQs disponíveis...
          </p>
        </div>
      )}
    </div>
  );
}